import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import "./BoardsDetailExploreSlider.css";
import { useTranslation } from "react-i18next";
import { getBoards } from "../../../../../api/boards";
import { getAccessories } from "../../../../../api/metals";
import { getMetalFrames } from "../../../../../api/metalFrames";
import { resolveMediaUrl } from "../../../../../utils/media";

import slideImageOne from "../../../../../assets/images/allprojects/exploreprojects/p3.jpg";
import slideImageTwo from "../../../../../assets/images/allprojects/exploreprojects/p2.jpg";
import slideImageThree from "../../../../../assets/images/allprojects/exploreprojects/p4.jpg";
import slideImageFour from "../../../../../assets/images/allprojects/exploreprojects/p5.jpg";
import leftArrowIcon from "../../../../../assets/icons/left.svg";
import rightArrowIcon from "../../../../../assets/icons/right.svg";

const FALLBACK_IMAGES = [slideImageOne, slideImageTwo, slideImageThree, slideImageFour];

const slugifyBoard = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const normalizeId = (value) => String(value ?? "").trim();

const handleCardClick = () => {
  if (typeof window !== "undefined") {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }
};

const BoardsDetailExploreSlider = () => {
  const { i18n } = useTranslation();
  const { pathname } = useLocation();
  const params = useParams();
  const railRef = useRef(null);
  const isRtl = i18n.dir(i18n.language) === "rtl";

  const [boards, setBoards] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [drywallFrames, setDrywallFrames] = useState([]);
  const [ceilingFrames, setCeilingFrames] = useState([]);

  const isBoardsDetail = pathname.startsWith("/products/boards/");
  const isAccessoryDetail =
    pathname.startsWith("/products/accessories/") ||
    pathname.startsWith("/products/metal/");
  const isDrywallFrameDetail = pathname.startsWith("/products/drywall-metal-frame/");
  const isCeilingFrameDetail = pathname.startsWith("/products/ceiling-metal-frame/");
  const isAccessoriesListing = pathname === "/accessories";

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      const language = String(i18n.language || "en").toLowerCase();
      try {
        const [boardsRes, accessoriesRes, drywallRes, ceilingRes] = await Promise.all([
          getBoards({ language }),
          getAccessories({ language }),
          getMetalFrames({ language, frameType: "drywall-metal-frame" }),
          getMetalFrames({ language, frameType: "ceiling-metal-frame" }),
        ]);
        if (!isMounted) return;
        setBoards(Array.isArray(boardsRes) ? boardsRes : []);
        setAccessories(Array.isArray(accessoriesRes) ? accessoriesRes : []);
        setDrywallFrames(Array.isArray(drywallRes) ? drywallRes : []);
        setCeilingFrames(Array.isArray(ceilingRes) ? ceilingRes : []);
      } catch {
        if (!isMounted) return;
        setBoards([]);
        setAccessories([]);
        setDrywallFrames([]);
        setCeilingFrames([]);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [i18n.language]);

  const exploreCards = useMemo(() => {
    const pickImage = (item, index) =>
      resolveMediaUrl(item?.imageUrl) || FALLBACK_IMAGES[index % FALLBACK_IMAGES.length];

    if (isBoardsDetail) {
      const activeId = slugifyBoard(params.boardId);
      const cards = boards
        .map((item, index) => {
          const rawSlug =
            item?.slug || item?.boardType || item?.type || item?.name || item?.title || item?.id;
          const id = slugifyBoard(rawSlug);
          if (!id) return null;
          return {
            key: `board-${id}`,
            id,
            title: item?.name || item?.title || item?.boardType || "Board",
            image: pickImage(item, index),
            to: `/products/boards/${id}`,
          };
        })
        .filter(Boolean)
        .filter((card) => !activeId || card.id !== activeId);
      if (cards.length) return cards;
    }

    if (isAccessoryDetail) {
      const activeId = normalizeId(params.metalId);
      const cards = accessories
        .map((item, index) => {
          const id = normalizeId(item?.accessoryKey || item?.id);
          if (!id) return null;
          return {
            key: `accessory-${id}`,
            id,
            title: item?.name || item?.heroTitle || "Accessory",
            image: pickImage(item, index),
            to: `/products/accessories/${id}`,
          };
        })
        .filter(Boolean)
        .filter((card) => !activeId || card.id !== activeId);
      if (cards.length) return cards;
    }

    if (isDrywallFrameDetail) {
      const activeId = normalizeId(params.frameId);
      const cards = drywallFrames
        .map((item, index) => {
          const id = normalizeId(item?.frameKey || item?.id);
          if (!id) return null;
          return {
            key: `drywall-${id}`,
            id,
            title: item?.name || item?.heroTitle || "Drywall Metal Frame",
            image: pickImage(item, index),
            to: `/products/drywall-metal-frame/${id}`,
          };
        })
        .filter(Boolean)
        .filter((card) => !activeId || card.id !== activeId);
      if (cards.length) return cards;
    }

    if (isCeilingFrameDetail) {
      const activeId = normalizeId(params.frameId);
      const cards = ceilingFrames
        .map((item, index) => {
          const id = normalizeId(item?.frameKey || item?.id);
          if (!id) return null;
          return {
            key: `ceiling-${id}`,
            id,
            title: item?.name || item?.heroTitle || "Ceiling Metal Frame",
            image: pickImage(item, index),
            to: `/products/ceiling-metal-frame/${id}`,
          };
        })
        .filter(Boolean)
        .filter((card) => !activeId || card.id !== activeId);
      if (cards.length) return cards;
    }

    if (isAccessoriesListing) {
      const cards = accessories
        .map((item, index) => {
          const id = normalizeId(item?.accessoryKey || item?.id);
          if (!id) return null;
          return {
            key: `accessory-${id}`,
            id,
            title: item?.name || item?.heroTitle || "Accessory",
            image: pickImage(item, index),
            to: `/products/accessories/${id}`,
          };
        })
        .filter(Boolean);
      if (cards.length) return cards;
    }

    const firstBoard = boards[0];
    const firstAccessory = accessories[0];
    const firstDrywall = drywallFrames[0];
    const firstCeiling = ceilingFrames[0];
    return [
      {
        key: "cat-boards",
        title: firstBoard?.name || firstBoard?.title || "Boards",
        image: slideImageOne,
        to: "/products/boards",
      },
      {
        key: "cat-accessories",
        title: firstAccessory?.name || firstAccessory?.heroTitle || "Accessories",
        image: slideImageTwo,
        to: "/accessories",
      },
      {
        key: "cat-drywall",
        title: firstDrywall?.name || firstDrywall?.heroTitle || "Drywall metal frame",
        image: slideImageThree,
        to: "/products/drywall-metal-frame",
      },
      {
        key: "cat-ceiling",
        title: firstCeiling?.name || firstCeiling?.heroTitle || "Ceiling metal frame",
        image: slideImageFour,
        to: "/products/ceiling-metal-frame",
      },
    ];
  }, [
    isBoardsDetail,
    isAccessoryDetail,
    isDrywallFrameDetail,
    isCeilingFrameDetail,
    isAccessoriesListing,
    boards,
    accessories,
    drywallFrames,
    ceilingFrames,
    params.boardId,
    params.metalId,
    params.frameId,
  ]);

  const scrollCards = (direction) => {
    if (!railRef.current) {
      return;
    }

    const firstCard = railRef.current.querySelector(".boardsDetailExploreSlider-card");
    if (!firstCard) {
      return;
    }

    const cardWidth = firstCard.getBoundingClientRect().width;
    const gap = 12;
    const normalizedDirection = isRtl ? direction * -1 : direction;
    railRef.current.scrollBy({
      left: normalizedDirection * (cardWidth + gap),
      behavior: "smooth",
    });
  };

  return (
    <section className="boardsDetailExploreSlider" aria-labelledby="boardsDetailExploreSlider-heading">
      <div className="boardsDetailExploreSlider-inner">
        <div className="boardsDetailExploreSlider-content">
          <span className="boardsDetailExploreSlider-tag">PRODUCTS</span>

          <h2 id="boardsDetailExploreSlider-heading" className="boardsDetailExploreSlider-title">
            Continue exploring
            <span>other products</span>
          </h2>

          <p className="boardsDetailExploreSlider-desc">
            We offer a full range of high-quality construction materials, including gypsum boards,
            drywall and ceiling metal profiles, and essential accessories.
          </p>

          <div className="boardsDetailExploreSlider-controls">
            <button
              type="button"
              className="boardsDetailExploreSlider-navBtn"
              aria-label="Previous products"
              onClick={() => scrollCards(-1)}
            >
              <img src={isRtl ? rightArrowIcon : leftArrowIcon} alt="" aria-hidden />
            </button>
            <button
              type="button"
              className="boardsDetailExploreSlider-navBtn"
              aria-label="Next products"
              onClick={() => scrollCards(1)}
            >
              <img src={isRtl ? leftArrowIcon : rightArrowIcon} alt="" aria-hidden />
            </button>
          </div>
        </div>

        <div className="boardsDetailExploreSlider-trackWrap">
          <div className="boardsDetailExploreSlider-track" ref={railRef}>
            {exploreCards.map((card) => (
              <Link
                to={card.to}
                key={card.key}
                className="boardsDetailExploreSlider-card"
                onClick={handleCardClick}
              >
                <img src={card.image} alt={card.title} className="boardsDetailExploreSlider-image" />
                <div className="boardsDetailExploreSlider-overlay" />
                <div className="boardsDetailExploreSlider-cardTitle">{card.title}</div>
                <div className="boardsDetailExploreSlider-cardFooter">
                  <span>Product details</span>
                  <span className="boardsDetailExploreSlider-arrow">{isRtl ? "←" : "→"}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BoardsDetailExploreSlider;
